/* eslint-disable react/jsx-no-bind */
import React, { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { closeDashboardDialog } from '@actions/dashboard-actions';
import { addToGroups } from '@actions/groups-actions';
import * as dialog from '@constants/dialogs';
import { autocompleteSearchStyles } from '@constants/mui-theme';
import AsyncInputSearch from '@shared/async-input-search';
import Dialog from '@shared/dialogs/dialog';
import DialogActions from '@shared/dialogs/dialog-actions';
import { prepareGroupsForBulkAdd } from '@utils/group-utils';
import EntityListItem from './entity-list-item';

const AddRecords = ({ groupTypeName }) => {
  const dispatch = useDispatch();

  const activeDialogs = useSelector(state => state.dashboard.activeDialogs);
  const dialogParams = useSelector(state => state.dashboard.dialogParams[dialog.ADD_RECORDS_TO_GROUP]);

  const [entities, setEntities] = useState([]);

  const handleCancel = useCallback(() => {
    dispatch(closeDashboardDialog(dialog.ADD_RECORDS_TO_GROUP));
  }, [dispatch]);

  const handleEntityListChange = useCallback(newValues => {
    const newEntities = newValues.map(newValue => newValue.value);
    setEntities(newEntities);
  }, []);

  const handleSubmit = useCallback(() => {
    const { group } = dialogParams;
    const payload = prepareGroupsForBulkAdd({
      selectedGroups: [group.id],
      entityIds: entities,
      includeOverlaps: false
    });
    dispatch(addToGroups(groupTypeName, payload, null, false, null, true));
    dispatch(closeDashboardDialog(dialog.ADD_RECORDS_TO_GROUP));
  }, [dialogParams, dispatch, entities, groupTypeName]);

  const toOptions = useCallback(results => results.suggestions.filter(
    suggestion => suggestion.type !== 'Group' && !suggestion.type.startsWith('Attribute_')
  ).map(
    ({ id, display, extra, type }) => ({ id, title: display, value: id, extra, type })
  ), []);

  if (!dialogParams || !dialogParams.group || !activeDialogs[dialog.ADD_RECORDS_TO_GROUP]) {
    return null;
  }

  return (
    <Dialog
      actions={
        <DialogActions
          disabledSubmit={entities.length === 0}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          submitLabel="ADD"
        />
      }
      title="Add records to group"
    >
      <AsyncInputSearch
        {...autocompleteSearchStyles.normal}
        avatarProps={{ type: 'entity' }}
        fieldName="entity_ids"
        onChange={handleEntityListChange}
        label="Search records"
        renderOption={(option, state) => <EntityListItem item={option} term={state.inputValue} />}
        toOptions={toOptions}
      />
    </Dialog>
  );
};

AddRecords.propTypes = {
  groupTypeName: PropTypes.string
};

export default memo(AddRecords);
